import {convertSearchResponse, Hotel, HotelListingAvailability, Search, SearchResponse} from "./hotels";
import * as moment from "moment";
import {User, Address} from "./user";
import {map} from "lodash";

export interface Totals {
    subTotal: number;
    taxTotal: number;
    discountTotal: number;
    grandTotal: number;
}

interface OrderCommon extends Totals {
    tickets: OrderTicket[];
    orderNumber?: number;
    orderStatus?: string;
    existingUserId?: string;
    newUser?: User;
    paymentInfo?: PaymentInfo;
    guests: Guest[];
    id?: string;
    user?: User;
    ticketsSelected: boolean;
    transactions: Transaction[];
    lastRequest: moment.Moment;
    createdOn?: string;
    discounts: OrderDiscount[];
    shippingTotal: number;
    insuranceTotal: number;
    shippingMethod: string;
    insuranceTickets: number;
    memoryMaker: boolean;
    memoryMakerTotal: number;
    geniePlus: boolean;
    geniePlusTotal: number;
    insuranceConfirmation: boolean;
    memoryMakerConfirmation: boolean;
    geniePlusConfirmation: boolean;
    shippingAddress?: Address,
    billingAddress?: Address
    form?: string;
    billingSameAsShipping: boolean;
}

export interface Order extends OrderCommon {
    search: Search;
    hotel: OrderHotel[];
}

export interface OrderUpdate {
    orderId: string;
    status?: string;
    clearCardData?: boolean;
}

export interface OrderTicket extends Totals {
    id?: string;
    ticketId: string;
    firstDate: string;
    quantityChildren: number;
    quantityAdults: number;
    ticketName?: string;
}

interface OrderHotelCommon extends Totals {
    id: string;
    roomId: string;
    hotelId: string;
    destination: string;
    availabilityId: string;
    numAdults: number;
    childAges: number[];
    hotelTitle?: string;
    roomTitle?: string;
    offerName?: string;
}

export interface OrderHotel extends OrderHotelCommon {
    checkIn: moment.Moment;
    checkOut: moment.Moment;
}

export interface OrderResponse extends OrderCommon {
    search: SearchResponse;
    hotel: OrderHotelResponse[];
}

export interface OrderHotelResponse extends OrderHotelCommon {
    checkIn: string;
    checkOut: string;
}

export interface PaymentInfo {
    cardFirstName: string;
    cardLastName: string;
    ccNum: string;
    cardType: string;
    expMonth: number;
    expYear: number;
    zipCode: string;
    cvv2: string;
    ccLast4: string;
}

export interface Guest {
    title: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    suffix?: string;
}

export interface OrderAndListings {
    order: OrderResponse;
    listings: HotelListingAvailability[];
}

export interface OrderAndDetail {
    order: OrderResponse;
    hotel?: Hotel;
}

export function convertOrderResponse(response: OrderResponse): Order {
    const hotels = map(response.hotel, ho => ({
        ...ho,
        checkIn: moment(ho.checkIn),
        checkOut: moment(ho.checkOut)
    } as OrderHotel));
    return {
        ...response,
        hotel: hotels,
        search: convertSearchResponse(response.search)
    } as Order;
}

export function prepOrderConfirmation(response: OrderResponse): Order {
    return {
        id: response.id,
        orderStatus: response.orderStatus,
        orderNumber: response.orderNumber,
        tickets: [],
        guests: [],
        ticketsSelected: false,
        transactions: [],
        lastRequest: moment(),
        hotel: [],
        search: {},
        user: response.user,
    } as Order;
}

export interface OrderValidationResults {
    order: OrderResponse;
    validationErrorMessage: string;
    isValid: boolean;
}

export interface OrderSearchParams {
    search?: string;
    minDate?: string;
    maxDate?: string;
}

export interface Transaction {
    id: string;
    cardType: string;
    cardFirstName: string;
    cardLastName: string;
    lastFour: string;
    amount: number;
    cardExpMonth: number;
    cardExpYear: number;
    cardZip: string;
    status: string;
    message: string;
    notes: string;
    haveNotes: boolean;
    createdOn: string;
    cvv2?: string;
}

export interface OrderDiscount {
    discountId: string;
    discountName: string;
}

export interface DiscountCode {
    id?: string | null;
    name: string;
    activeFrom: string;
    activeTo: string;
    discountFixed?: number | null;
    discountPercent?: number | null;
    active: boolean;
    perTicket: boolean;
    createdOn?: string;
}

export const defaultDiscountCode = () => ({
    name: "",
    activeFrom: "",
    activeTo: "",
    active: true
} as DiscountCode);

export interface TicketDatePrice {
    id: string;
    firstDate: string;
    ticketId: string;
    adultPrice: number;
    childPrice: number;
}

export interface OrderTicketPriceInfo {
    orderTicket: OrderTicket;
    datePrice: TicketDatePrice;
    lastDate: string;
    adultSubtotal: number;
    childSubtotal: number;
}

export interface TicketPriceDelta {
    id?: string;
    ticketId: string;
    form: string;
    multiplier: number;
    adultAdjustment: number;
    childAdjustment: number;
}



// WEBPACK FOOTER //
// ./src/models/order.ts